import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonSets = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Hash Tables</div>
                <h1>Sets: Hashing & Fast Lookups</h1>
                <p className="hero-lead">
                    A Set is not just a "list without duplicates". It's a high-performance mathematical tool backed by Hash Tables.
                    It trades memory for speed, offering O(1) lookups that can be 1000x faster than Lists.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    If you have a badge ID <code>12345</code> and you want to check if it's in a list of 1 million IDs, Python has to scan them one by one.
                    That's <strong>O(N)</strong> (Linear Time).
                </p>
                <p>
                    If you use a Set, Python hashes <code>12345</code>  <code>0xAF32...</code> and jumps directly to that memory slot.
                    That's <strong>O(1)</strong> (Constant Time). Size doesn't matter.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li><strong>The Magic:</strong> How Hash Tables allow instantaneous lookups.</li>
                        <li><strong>Venn Diagrams:</strong> Using <code>|</code>, <code>&</code>, <code>-</code>, and <code>^</code> for logic.</li>
                        <li><strong>Hashability:</strong> Why you can't put a list inside a set.</li>
                        <li><strong>Frozensets:</strong> The immutable set (usable as a dictionary key).</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>The Power of Mathematics (Set Ops)</h2>
                <p>
                    Sets aren't just for storage; they are for logic. You can perform complex filtering using mathematical operators.
                </p>
                <div className="analogy-box">
                    <p>
                        Imagine strict permissions: "Users who are Admins BUT NOT Editors".
                        In loops, this is 10 lines of code. With sets, it's 1 symbol based on Venn Diagrams.
                    </p>
                </div>

                <div className="code-example">
                    <div className="code-header">
                        <span>Binary Logic Refactored</span>
                    </div>
                    <CodeBlock language="python" code={`engineers = {"Alice", "Bob", "Charlie", "Dave"}
managers = {"Charlie", "Dave", "Eve"}

# 1. Union (|): Everyone involved
print(engineers | managers)
# {'Alice', 'Bob', 'Charlie', 'Dave', 'Eve'}

# 2. Intersection (&): The Engineers who are ALSO Managers
print(engineers & managers)
# {'Charlie', 'Dave'}

# 3. Difference (-): Engineers who are NOT Managers
print(engineers - managers)
# {'Alice', 'Bob'}

# 4. Symmetric Difference (^): People who are ONE role, but NOT both
print(engineers ^ managers)
# {'Alice', 'Bob', 'Eve'}`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The Cost of Speed: Hashing</h2>
                <p>
                    Sets are fast because they rely on <code>hash()</code>.
                    This introduces a strict rule: <strong>Set elements must be immutable (hashable).</strong>
                </p>
                <p>
                    You can put an integer, string, or tuple into a set.
                    You <strong>cannot</strong> put a list or dictionary into a set, because if the list changed, its hash would break the lookup logic.
                </p>

                <CodeBlock language="python" code={`# Valid: Tuple is immutable
s = {(1, 2), (3, 4)}

#  Invalid: List is mutable
# TypeError: unhashable type: 'list'
s = {[1, 2], [3, 4]}`} />

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>No Order Guarantee:</strong> Before Python 3.7, sets were completely random. Now they are... "implementation dependent". Never rely on the order of a set. If you need order + uniqueness, use a <code>dict</code> keys.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Performance Case Study</h2>
                <p>
                    Why bother converting a list to a set? Let's check the speed difference.
                </p>
                <CodeBlock language="python" code={`# Looking for a needle in a haystack of size 10,000,000

# LIST Lookup (O(N))
# Python must check 10 million items one by one.
# Time: ~1.5 seconds

# SET Lookup (O(1))
# Python calculates hash("needle") and jumps there.
# Time: ~0.00001 seconds

# Conclusion: If you are doing frequent lookups (ex: "if x in collection"), ALWAYS uses a Set.`} />
            </div>


            <div className="content-section best-practices">
                <h2>Summary & Best Practices</h2>
                <div className="practice-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Use sets for large "allow-lists" or "block-lists".</li>
                            <li>Use <code>set(list)</code> to quickly remove duplicates (deduplication).</li>
                            <li>Use <code>frozenset()</code> if you need a set as a dict key.</li>
                        </ul>
                    </div>

                    <div className="practice-card">
                        <h3> Don't</h3>
                        <ul>
                            <li>Don't assume sets preserve insertion order.</li>
                            <li>Don't try to modify a set while iterating over it (RuntimeError).</li>
                        </ul>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default PythonSets;

